import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Crown, Check, Sparkles, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const PremiumModal = ({ isOpen, onClose, variant = 'generic' }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const content = {
    fr: {
      generic: {
        title: "Contenu Premium",
        message: "Débloque l'accès complet pour continuer ta progression.",
        cta: "Passer à Premium"
      },
      transversal: {
        title: "Approfondir les thèmes universels",
        message: "Les parcours transversaux permettent de relier les traditions et d'aller plus loin dans la compréhension.",
        cta: "Débloquer Transversal"
      },
      benefits: [
        "Accès illimité à tous les cours",
        "Déblocage complet du cours Transversal",
        "Accès à tous les parcours et leçons",
        "Progression sans limite",
        "Soutien au développement de Spirify"
      ],
      price: "4,99€/mois",
      cancel: "Plus tard"
    },
    en: {
      generic: {
        title: "Premium Content",
        message: "Unlock full access to continue your progress.",
        cta: "Go Premium"
      },
      transversal: {
        title: "Explore Universal Themes",
        message: "Transversal courses connect traditions and deepen understanding.",
        cta: "Unlock Transversal"
      },
      benefits: [
        "Unlimited access to all courses",
        "Full Transversal course unlock",
        "Access to all paths and lessons",
        "Unlimited progression",
        "Support Spirify's development"
      ],
      price: "€4.99/month",
      cancel: "Later"
    },
    es: {
      generic: {
        title: "Contenido Premium",
        message: "Desbloquea el acceso completo para continuar tu progreso.",
        cta: "Pasar a Premium"
      },
      transversal: {
        title: "Explorar temas universales",
        message: "Los cursos transversales conectan tradiciones y profundizan la comprensión.",
        cta: "Desbloquear Transversal"
      },
      benefits: [
        "Acceso ilimitado a todos los cursos",
        "Desbloqueo completo del curso Transversal",
        "Acceso a todos los recorridos y lecciones",
        "Progresión sin límites",
        "Apoyo al desarrollo de Spirify"
      ],
      price: "4,99€/mes",
      cancel: "Más tarde"
    },
    it: {
      generic: {
        title: "Contenuto Premium",
        message: "Sblocca l'accesso completo per continuare la tua progressione.",
        cta: "Passa a Premium"
      },
      transversal: {
        title: "Approfondire i temi universali",
        message: "I percorsi trasversali collegano le tradizioni e approfondiscono la comprensione.",
        cta: "Sblocca Transversal"
      },
      benefits: [
        "Accesso illimitato a tutti i corsi",
        "Sblocco completo del corso Transversal",
        "Accesso a tutti i percorsi e lezioni",
        "Progressione senza limiti",
        "Supporto allo sviluppo di Spirify"
      ],
      price: "4,99€/mese",
      cancel: "Più tardi"
    },
    pt: {
      generic: {
        title: "Conteúdo Premium",
        message: "Desbloqueie o acesso completo para continuar sua progressão.",
        cta: "Passar para Premium"
      },
      transversal: {
        title: "Explorar temas universais",
        message: "Os cursos transversais conectam tradições e aprofundam a compreensão.",
        cta: "Desbloquear Transversal"
      },
      benefits: [
        "Acesso ilimitado a todos os cursos",
        "Desbloqueio completo do curso Transversal",
        "Acesso a todos os percursos e lições",
        "Progressão sem limites",
        "Apoio ao desenvolvimento do Spirify"
      ],
      price: "4,99€/mês",
      cancel: "Mais tarde"
    },
    ar: {
      generic: {
        title: "محتوى مميز",
        message: "افتح الوصول الكامل لمواصلة تقدمك.",
        cta: "الترقية للمميز"
      },
      transversal: {
        title: "استكشاف المواضيع العالمية",
        message: "الدورات العابرة تربط التقاليد وتعمق الفهم.",
        cta: "فتح Transversal"
      },
      benefits: [
        "وصول غير محدود لجميع الدورات",
        "فتح كامل لدورة Transversal",
        "الوصول لجميع المسارات والدروس",
        "تقدم بلا حدود",
        "دعم تطوير Spirify"
      ],
      price: "4.99€/شهر",
      cancel: "لاحقاً"
    }
  };

  const t = content[language] || content.fr;
  const variantContent = t[variant] || t.generic;

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-purple-50 to-indigo-50" data-testid="premium-modal">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {variantContent.title}
          </DialogTitle>
          <DialogDescription className="text-gray-600 mt-2">
            {variantContent.message}
          </DialogDescription>
        </DialogHeader>

        <div className="my-6 space-y-3">
          {t.benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-sm text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="font-bold text-purple-700">{t.price}</span>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-6"
            data-testid="premium-cta-button"
          >
            <Crown className="w-5 h-5 mr-2" />
            {variantContent.cta}
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full text-gray-500"
            data-testid="premium-cancel-button"
          >
            {t.cancel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumModal;
